import * as React from 'react';
import {noop} from 'lodash';

import UiTab from './Tab';
import TabList from './TabList';
import type {CSSProperties} from '@packages/utilities/css-properties-type';

const {useContext, useState} = React;

type TabStateContext = {
  activeTabId: string | null;
  onSelectTab: (activeTabId: string, disabled?: boolean) => () => void;
};

const defaultTabState: TabStateContext = {
  activeTabId: null,
  onSelectTab: (_) => noop,
};

const TabControlContext = React.createContext(defaultTabState);

TabControlContext.displayName = 'TabControlContext';

/**
 * @deprecated use `Tabs`, `TabList`, `Tab`, and `TabPanel` from `@packages/systems/spring` instead
 */
const Consumer = TabControlContext.Consumer;

type TabProps = {
  disabled?: boolean;
  id?: string;
  children: React.ReactNode;
  style?: CSSProperties;
  ['data-automation-id']?: string;
};

/**
 * @deprecated use `Tabs`, `TabList`, `Tab`, and `TabPanel` from `@packages/systems/spring` instead
 */
const Tab = React.memo(
  ({disabled, id, style, children, 'data-automation-id': dataAutomationId}) => {
    const {activeTabId, onSelectTab} = useContext(TabControlContext);

    const handleClick = () => {
      // condition to make flow happy
      if (id) {
        onSelectTab(id, disabled);
      }
    };

    return (
      <UiTab
        disabled={disabled}
        isCurrent={!disabled && id === activeTabId}
        onClick={handleClick}
        style={style}
        data-automation-id={dataAutomationId}
      >
        {children}
      </UiTab>
    );
  }
) as React.ComponentType<TabProps>;

/*
 * This Tab control component is inspired and built using Ryan Florence's
 * controlled component pattern. Watch his advanced react training for free
 * at https://courses.totalreact.com/p/advanced-react-free
 */

type Props = {
  defaultActiveId?: string;
  activeTabId?: string;
  onChange?: (id: string) => void;
  children: React.ReactNode;
};

/**
 * @deprecated use `Tabs`, `TabList`, `Tab`, and `TabPanel` from `@packages/systems/spring` instead
 */
const TabControl = React.memo(
  ({defaultActiveId, activeTabId, onChange, children}) => {
    const activeTabIdProp = activeTabId;

    // If an activeTabId is passed in, this indicates that the Tabs are being controlled by an external component.
    const isControlled = Boolean(activeTabIdProp);

    // If the tabs are being controlled by an external component, we'll use the passed in activeTabId to set the state.
    const [activeTabIdState, setActiveTab] = useState(
      activeTabIdProp || defaultActiveId
    );

    const selectTab = (tabId: string, disabled?: boolean) => {
      if (!disabled) {
        setActiveTab(tabId);
        if (onChange) {
          onChange(tabId);
        }
      }
    };

    return (
      <TabControlContext.Provider
        value={{
          // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string | null'.
          activeTabId: isControlled ? activeTabIdProp : activeTabIdState,
          // @ts-expect-error - TS2322 - Type '(tabId: string, disabled?: boolean) => void' is not assignable to type '(activeTabId: string, disabled?: boolean | undefined) => () => void'.
          onSelectTab: selectTab,
        }}
      >
        {children}
      </TabControlContext.Provider>
    );
  }
) as React.ComponentType<Props>;

export {TabControl, Tab, TabList, Consumer};
